import { observeUntilAdVisible } from './firstAdVisible'
import type { AdType } from './firstAdVisible'

/**
 * Each ad request's lifecycle and outcome, sent to Datadog RUM as duration vitals next to `first_ad_visible`, so a slow
 * or missing first ad can be traced to the step that held it up or the slot that stayed empty.
 *
 * - `ad_slot_request`: one GPT request cycle of one slot, from `slotRequested` to `slotRenderEnded`, with
 *   `slotResponseReceived` and `slotOnload` as offsets. GPT's events carry the slot but no request id, so a step is
 *   matched to a cycle only when one open cycle of that slot can own it. When a slot was requested again before the
 *   last cycle finished and a step could belong to either cycle, those cycles go out as `ambiguous` instead of being
 *   guessed,
 *   and the steps they still owe are absorbed rather than matched to a newer request. A cycle that timed out owes its
 *   missing steps the same way. GPT sets no cutoff for a late callback, so time passing proves nothing: a missing step
 *   is kept until it arrives or the slot has been requested `MAX_STALE_DEBT_REQUESTS` more times. Until the owed step
 *   is matched or written off, a later cycle of the slot that it could belong to is marked `ambiguous` (for a missing
 *   onload, the later cycle only goes out without `onload_ms`). A written-off step is counted in the summary, so a step
 *   that never comes taints a few cycles, not the rest of the page.
 * - `native_widget_render`: one Outbrain, Taboola or Primis widget, from the moment the component asked the vendor for
 *   it until the vendor's render signal, with the moment it first counted as visible as an offset. For Primis that
 *   signal is the player script loading, not a confirmed creative.
 * - `ad_lifecycle_summary`: once, when the page stops being measured, with how many requests were seen and how many
 *   were dropped.
 *
 * Same names and tags as Qz Core, so the two can be read side by side. Read only: nothing here changes when or how an
 * ad is requested or rendered.
 */

export const AD_SLOT_REQUEST_VITAL = 'ad_slot_request'
export const NATIVE_WIDGET_RENDER_VITAL = 'native_widget_render'
export const AD_LIFECYCLE_SUMMARY_VITAL = 'ad_lifecycle_summary'

/** Slot vitals per page load, unmatched and ambiguous ones included. The rest are counted. */
export const MAX_SLOT_VITALS = 8
/** Native widget vitals per page load. The rest are counted. */
export const MAX_NATIVE_VITALS = 4
/** Cycles of one slot kept open at once. A request past it ends the oldest. */
export const MAX_OPEN_CYCLES_PER_SLOT = 3
/** Later requests of a slot a step it still owes can taint. Past that the step is written off and counted. */
export const MAX_STALE_DEBT_REQUESTS = 3
/** A slot with no response, or a response and no render, by then gave up. */
export const SLOT_RESPONSE_MAX_MS = 30_000
/** How long a filled slot waits for its creative's `slotOnload`. */
export const SLOT_ONLOAD_WAIT_MS = 10_000
/** A widget the vendor has not rendered by then counts as not rendered. */
export const NATIVE_RENDER_MAX_MS = 30_000
/** A rendered widget waits this long from its request to come into view. */
export const NATIVE_VISIBLE_MAX_MS = 60_000

export type AdSlotOutcome =
  | 'filled'
  | 'empty'
  | 'no_response'
  | 'no_render'
  | 'unmatched'
  | 'ambiguous'
export type NativeAdType = Exclude<AdType, 'display'>

export interface AdLifecycleVital {
  name: string
  /** Epoch ms. */
  startTime: number
  duration: number
  context: Record<string, string | number>
}

/** The parts of a GPT slot read here. Never written to. */
export interface LifecycleSlot {
  getAdUnitPath: () => string
}

type SlotStep = 'response' | 'render'

interface SlotCycle {
  placement: string
  refresh: boolean
  requestedAt: number
  respondedAt?: number
  renderedAt?: number
  isEmpty?: boolean
  onloadAt?: number
  // Its onload could have been another cycle's, so it goes out without one.
  onloadUnknown: boolean
  // The request an older cycle's missing onload dates from, when that onload
  // was absorbed in place of this one's: the onload still to come is either, so
  // any onload this cycle ends up owing keeps the older age and still writes off.
  onloadDebtRequest?: number
  fallback: boolean
  // Over the cap: kept so its later events still match, but never sent.
  dropped: boolean
  timer?: ReturnType<typeof setTimeout>
}

// The steps one cycle that already went out (timed out, ambiguous, or ended by the per-slot bound) may still have GPT
// report, so they can't be matched to a newer cycle of the same slot. `request` is the slot's request count when it went
// out, which is what writes it off.
interface OwedSteps {
  response: number
  render: number
  onload: number
  request: number
}

interface NativeWidget {
  adType: NativeAdType
  placement: string
  element: Element
  requestedAt: number
  renderedAt?: number
  visibleAt?: number
  timer?: ReturnType<typeof setTimeout>
  stopObserving?: () => void
}

export interface AdRequestLifecycleTracker {
  slotRequested: (slot: LifecycleSlot) => void
  slotResponseReceived: (slot: LifecycleSlot) => void
  slotRenderEnded: (slot: LifecycleSlot, isEmpty: boolean) => void
  slotOnload: (slot: LifecycleSlot) => void
  /** The empty slot was handed to a native fallback. */
  slotFellBack: (slot: LifecycleSlot) => void
  /** Returns whether the widget is measured, so its render is worth listening for. */
  nativeRequested: (
    adType: NativeAdType,
    placement: string,
    element: Element
  ) => boolean
  nativeRendered: (element: Element) => void
  /** Ends the page load: sends what has an outcome, drops the rest. */
  stop: () => void
  readonly stopped: boolean
}

const round = (ms: number) => Math.max(0, Math.round(ms))

export function createAdRequestLifecycleTracker({
  now,
  timeOrigin,
  pageStart,
  send,
  context,
  observeVisible,
  onStop,
}: {
  /** Ms on the performance clock. */
  now: () => number
  /** Epoch ms of the performance clock's zero. */
  timeOrigin: number
  /** Ms on the performance clock the visitor started seeing the page. */
  pageStart: () => number
  send: (vital: AdLifecycleVital) => void
  /** Tags every vital carries, read when it is sent. */
  context: () => Record<string, string>
  /** Calls `onVisible` once the element counts as on screen. Returns a function that stops watching. */
  observeVisible: (element: Element, onVisible: () => void) => () => void
  onStop?: () => void
}): AdRequestLifecycleTracker {
  let stopped = false
  let slotBudget = MAX_SLOT_VITALS
  let nativeBudget = MAX_NATIVE_VITALS
  const counts = {
    slot_requests: 0,
    slot_dropped: 0,
    slot_steps_written_off: 0,
    native_requests: 0,
    native_dropped: 0,
  }
  const requestsPerSlot = new WeakMap<LifecycleSlot, number>()
  // Open cycles of each slot, oldest first. A slot leaves the map once it has none.
  const openCycles = new Map<LifecycleSlot, SlotCycle[]>()
  // Per slot, oldest first. Bounded: each entry is written off `MAX_STALE_DEBT_REQUESTS` requests after it was made.
  const owedSteps = new WeakMap<LifecycleSlot, OwedSteps[]>()
  // A fallback reported before this listener saw the empty render, for the render of the same GPT event.
  const fallbackPending = new Set<LifecycleSlot>()
  // Every widget ever requested, so a repeat request is ignored without keeping the element.
  const seenWidgets = new WeakSet<Element>()
  // Only the widgets being measured, until they are sent.
  const widgets = new Map<Element, NativeWidget>()

  const emit = (
    name: string,
    start: number,
    duration: number,
    tags: Record<string, string | number>
  ) => {
    try {
      send({
        name,
        startTime: Math.round(timeOrigin + start),
        duration: round(duration),
        context: { ...tags, ...context() },
      })
    } catch {
      // A measurement only: lose it, keep the page.
    }
  }

  const cyclesOf = (slot: LifecycleSlot) => openCycles.get(slot) ?? []

  const removeCycle = (slot: LifecycleSlot, cycle: SlotCycle) => {
    clearTimeout(cycle.timer)
    const remaining = cyclesOf(slot).filter((open) => open !== cycle)
    if (remaining.length > 0) openCycles.set(slot, remaining)
    else openCycles.delete(slot)
  }

  const sendCycle = (
    slot: LifecycleSlot,
    cycle: SlotCycle,
    outcome: AdSlotOutcome,
    extra: Record<string, string> = {}
  ) => {
    if (!cyclesOf(slot).includes(cycle)) return
    removeCycle(slot, cycle)
    if (cycle.dropped) return
    const tags: Record<string, string | number> = {
      outcome,
      placement: cycle.placement,
      request_cycle: cycle.refresh ? 'refresh' : 'first',
      fallback: cycle.fallback ? 'native' : 'none',
      ...extra,
    }
    if (cycle.respondedAt !== undefined)
      tags.response_ms = round(cycle.respondedAt - cycle.requestedAt)
    if (cycle.renderedAt !== undefined)
      tags.render_ms = round(cycle.renderedAt - cycle.requestedAt)
    if (cycle.onloadAt !== undefined)
      tags.onload_ms = round(cycle.onloadAt - cycle.requestedAt)
    const end = cycle.renderedAt ?? now()
    emit(
      AD_SLOT_REQUEST_VITAL,
      cycle.requestedAt,
      end - cycle.requestedAt,
      tags
    )
  }

  // The outcome a cycle ends with when nothing more arrives for it.
  const outcomeOf = (cycle: SlotCycle): AdSlotOutcome => {
    if (cycle.renderedAt !== undefined)
      return cycle.isEmpty ? 'empty' : 'filled'
    return cycle.respondedAt === undefined ? 'no_response' : 'no_render'
  }

  const settleLater = (slot: LifecycleSlot, cycle: SlotCycle, ms: number) => {
    clearTimeout(cycle.timer)
    // A cycle that runs out of time can still have steps on their way, so it ends owing them.
    cycle.timer = setTimeout(() => endOwing(slot, cycle, outcomeOf(cycle)), ms)
  }

  const owedCount = (slot: LifecycleSlot, step: SlotStep | 'onload') =>
    (owedSteps.get(slot) ?? []).reduce((total, owed) => total + owed[step], 0)

  // Takes a step that belongs to a cycle already sent, from the newest one owing it, so an old step that never comes
  // still ages out. Returns the request the owed step dates from, or nothing when none was owed. An empty render also
  // means that cycle's creative won't load.
  const absorb = (
    slot: LifecycleSlot,
    step: SlotStep | 'onload',
    isEmpty?: boolean
  ): number | undefined => {
    const entries = owedSteps.get(slot) ?? []
    const owed = [...entries].reverse().find((entry) => entry[step] > 0)
    if (!owed) return undefined
    owed[step] -= 1
    if (step === 'render' && isEmpty && owed.onload > 0) owed.onload -= 1
    const remaining = entries.filter(
      (entry) => entry.response + entry.render + entry.onload > 0
    )
    if (remaining.length > 0) owedSteps.set(slot, remaining)
    else owedSteps.delete(slot)
    return owed.request
  }

  // Writes off what the slot owes from more than `MAX_STALE_DEBT_REQUESTS` requests ago.
  const writeOffStale = (slot: LifecycleSlot, request: number) => {
    const entries = owedSteps.get(slot)
    if (!entries) return
    const live = entries.filter((owed) => {
      if (request - owed.request <= MAX_STALE_DEBT_REQUESTS) return true
      counts.slot_steps_written_off += owed.response + owed.render + owed.onload
      return false
    })
    if (live.length > 0) owedSteps.set(slot, live)
    else owedSteps.delete(slot)
  }

  // Sends a cycle that is ending before GPT reported all of it (it timed out, was ambiguous, or was ended by the
  // per-slot bound), and remembers what GPT may still report for it.
  const endOwing = (
    slot: LifecycleSlot,
    cycle: SlotCycle,
    outcome: AdSlotOutcome,
    extra: Record<string, string> = {}
  ) => {
    const response =
      cycle.respondedAt === undefined && cycle.renderedAt === undefined ? 1 : 0
    const render = cycle.renderedAt === undefined ? 1 : 0
    const onload = cycle.onloadAt === undefined && !cycle.isEmpty ? 1 : 0
    if (response + render + onload > 0) {
      // A cycle that was marked unknown for an older cycle's onload owes it at
      // that older age (it has rendered, so the onload is all it can owe).
      const request = cycle.onloadDebtRequest ?? requestsPerSlot.get(slot) ?? 0
      owedSteps.set(slot, [
        ...(owedSteps.get(slot) ?? []),
        { response, render, onload, request },
      ])
    }
    sendCycle(slot, cycle, outcome, extra)
  }

  const sendUnmatched = (slot: LifecycleSlot, stage: SlotStep) => {
    if (slotBudget <= 0) {
      counts.slot_dropped += 1
      return
    }
    slotBudget -= 1
    emit(AD_SLOT_REQUEST_VITAL, now(), 0, {
      outcome: 'unmatched',
      placement: slot.getAdUnitPath(),
      stage,
    })
  }

  // The one open cycle this step belongs to, or nothing when it can't be told (the step is then handled here).
  const matchStep = (
    slot: LifecycleSlot,
    step: SlotStep,
    isEmpty?: boolean
  ): SlotCycle | undefined => {
    const waiting = cyclesOf(slot).filter((cycle) =>
      step === 'response'
        ? cycle.respondedAt === undefined && cycle.renderedAt === undefined
        : cycle.renderedAt === undefined
    )
    const owed = owedCount(slot, step)
    if (waiting.length === 1 && owed === 0) return waiting[0]
    if (waiting.length === 0) {
      if (absorb(slot, step, isEmpty) === undefined) sendUnmatched(slot, step)
      return undefined
    }
    // Two or more cycles could own it: none gets a guessed timing.
    waiting.forEach((cycle) =>
      endOwing(slot, cycle, 'ambiguous', { ambiguous_step: step })
    )
    absorb(slot, step, isEmpty)
    return undefined
  }

  const sendWidget = (widget: NativeWidget) => {
    if (widgets.get(widget.element) !== widget) return
    widgets.delete(widget.element)
    clearTimeout(widget.timer)
    widget.stopObserving?.()
    const rendered = widget.renderedAt !== undefined
    const tags: Record<string, string | number> = {
      ad_type: widget.adType,
      placement: widget.placement,
      outcome: rendered ? 'rendered' : 'no_render',
    }
    if (widget.visibleAt !== undefined)
      tags.visible_ms = round(widget.visibleAt - widget.requestedAt)
    const end = widget.renderedAt ?? widget.requestedAt + NATIVE_RENDER_MAX_MS
    emit(
      NATIVE_WIDGET_RENDER_VITAL,
      widget.requestedAt,
      end - widget.requestedAt,
      tags
    )
  }

  return {
    slotRequested(slot) {
      if (stopped) return
      const open = cyclesOf(slot)
      const oldest = open[0]
      if (oldest && open.length >= MAX_OPEN_CYCLES_PER_SLOT)
        endOwing(slot, oldest, outcomeOf(oldest))
      const count = (requestsPerSlot.get(slot) ?? 0) + 1
      requestsPerSlot.set(slot, count)
      writeOffStale(slot, count)
      counts.slot_requests += 1
      const dropped = slotBudget <= 0
      if (dropped) counts.slot_dropped += 1
      else slotBudget -= 1
      const cycle: SlotCycle = {
        placement: slot.getAdUnitPath(),
        refresh: count > 1,
        requestedAt: now(),
        onloadUnknown: false,
        fallback: false,
        dropped,
      }
      openCycles.set(slot, [...cyclesOf(slot), cycle])
      settleLater(slot, cycle, SLOT_RESPONSE_MAX_MS)
    },
    slotResponseReceived(slot) {
      if (stopped) return
      const cycle = matchStep(slot, 'response')
      if (cycle) cycle.respondedAt = now()
    },
    slotRenderEnded(slot, isEmpty) {
      if (stopped) return
      const cycle = matchStep(slot, 'render', isEmpty)
      if (!cycle) return
      cycle.renderedAt = now()
      cycle.isEmpty = isEmpty
      if (isEmpty && fallbackPending.has(slot)) cycle.fallback = true
      // An empty slot is handed to its fallback by another listener on the same event, so it settles once that
      // listener had its turn.
      settleLater(slot, cycle, isEmpty ? 0 : SLOT_ONLOAD_WAIT_MS)
    },
    slotOnload(slot) {
      if (stopped) return
      const waiting = cyclesOf(slot).filter(
        (cycle) =>
          cycle.renderedAt !== undefined &&
          !cycle.isEmpty &&
          cycle.onloadAt === undefined
      )
      // Onload is optional, so one that can't be told apart is dropped rather than guessed, and one arriving after
      // its cycle went out is not worth a vital of its own.
      const owedFrom = absorb(slot, 'onload')
      if (owedFrom !== undefined) {
        waiting.forEach((cycle) => {
          cycle.onloadUnknown = true
          cycle.onloadDebtRequest = Math.min(
            cycle.onloadDebtRequest ?? owedFrom,
            owedFrom
          )
        })
        return
      }
      if (waiting.length !== 1) {
        waiting.forEach((cycle) => (cycle.onloadUnknown = true))
        return
      }
      const [cycle] = waiting
      if (!cycle || cycle.onloadUnknown) return
      cycle.onloadAt = now()
      sendCycle(slot, cycle, 'filled')
    },
    slotFellBack(slot) {
      const empty = cyclesOf(slot).filter((cycle) => cycle.isEmpty)
      empty.forEach((cycle) => (cycle.fallback = true))
      if (empty.length > 0) return
      // The fallback listener ran first: the empty render of this same GPT event is still to come here.
      fallbackPending.add(slot)
      queueMicrotask(() => fallbackPending.delete(slot))
    },
    nativeRequested(adType, placement, element) {
      if (stopped || seenWidgets.has(element)) return false
      seenWidgets.add(element)
      counts.native_requests += 1
      if (nativeBudget <= 0) {
        counts.native_dropped += 1
        return false
      }
      nativeBudget -= 1
      const widget: NativeWidget = {
        adType,
        placement,
        element,
        requestedAt: now(),
      }
      widgets.set(element, widget)
      widget.timer = setTimeout(() => sendWidget(widget), NATIVE_RENDER_MAX_MS)
      return true
    },
    nativeRendered(element) {
      if (stopped) return
      const widget = widgets.get(element)
      if (!widget || widget.renderedAt !== undefined) return
      widget.renderedAt = now()
      clearTimeout(widget.timer)
      widget.timer = setTimeout(
        () => sendWidget(widget),
        Math.max(0, widget.requestedAt + NATIVE_VISIBLE_MAX_MS - now())
      )
      try {
        widget.stopObserving = observeVisible(element, () => {
          if (widgets.get(element) !== widget) return
          widget.visibleAt = now()
          sendWidget(widget)
        })
      } catch {
        // Without an observer the widget still reports, without visible_ms.
      }
    },
    stop() {
      if (stopped) return
      stopped = true
      openCycles.forEach((cycles, slot) =>
        cycles.forEach((cycle) => {
          if (cycle.renderedAt !== undefined) {
            sendCycle(slot, cycle, outcomeOf(cycle))
            return
          }
          clearTimeout(cycle.timer)
          if (!cycle.dropped) counts.slot_dropped += 1
        })
      )
      openCycles.clear()
      widgets.forEach((widget) => {
        if (widget.renderedAt !== undefined) {
          sendWidget(widget)
          return
        }
        clearTimeout(widget.timer)
        counts.native_dropped += 1
      })
      widgets.clear()
      if (counts.slot_requests + counts.native_requests > 0) {
        const start = pageStart()
        emit(AD_LIFECYCLE_SUMMARY_VITAL, start, now() - start, { ...counts })
      }
      onStop?.()
    },
    get stopped() {
      return stopped
    },
  }
}

/**
 * Calls `onVisible` the first time `element` counts as on screen, by the same rule and the same observer as
 * `first_ad_visible`, which follows the container as it gains height or grows. Returns a function that stops watching.
 */
export function observeAdVisible(
  element: Element,
  onVisible: () => void
): () => void {
  if (typeof IntersectionObserver === 'undefined') return () => {}
  return observeUntilAdVisible(element, onVisible)
}

let activeTracker: AdRequestLifecycleTracker | undefined

/** Set when the page load starts being measured. Nothing reports until it is. */
export function setAdRequestLifecycleTracker(
  tracker: AdRequestLifecycleTracker | undefined
) {
  activeTracker = tracker
}

/**
 * A native ad component asked its vendor for a widget to fill `element`. Call
 * it once, right where the request is made. Never throws.
 */
export function reportNativeAdRequest(
  adType: NativeAdType,
  placement: string,
  element: Element | null | undefined
) {
  try {
    if (element) activeTracker?.nativeRequested(adType, placement, element)
  } catch {
    // A measurement only: lose it, keep the ad.
  }
}

/** The vendor reported the widget in `element` rendered. Never throws. */
export function reportNativeAdRendered(element: Element | null | undefined) {
  try {
    if (element) activeTracker?.nativeRendered(element)
  } catch {
    // A measurement only: lose it, keep the ad.
  }
}

/** An empty display slot was handed to a native fallback. Never throws. */
export function reportAdSlotFallback(slot: LifecycleSlot) {
  try {
    activeTracker?.slotFellBack(slot)
  } catch {
    // A measurement only: lose it, keep the fallback.
  }
}
